import { FileObject } from "types/Models/fileObject";
import { emptyApi } from "./emptyApi";

export type DocumentRequest = {
  uuid: string;
  group_id: string;
  message_id: string;
  status: "SUBMITTED" | "REQUESTED";
  financial_closing_id?: string | null;
  requested_documents: [
    {
      uuid: string;
      name: string;
      other_name?: string;
      document_type_id: string;
      description: string;
      document: FileObject | null;
      documents: FileObject[];
      is_uploaded: boolean;
    }
  ];
};

export type DocumentRequestPayload = {
  channel_id: string;
  request_data: {
    document_type_id: string;
    description: string;
  }[];
  financial_closing_id?: string;
};

export type LinkableFinancialClosing = {
  uuid: string;
  start_date: string;
  end_date: string;
  status: string;
  accounting_method: string;
  task: { uuid: string; title: string } | null;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllDocumentTypes: build.query<
      { uuid: string; name: string }[],
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/messaging/group/${groupId}/document-type/`,
    }),
    getLinkableFinancialClosings: build.query<
      LinkableFinancialClosing[],
      { groupId: string; channelId: string }
    >({
      query: ({ groupId, channelId }) => ({
        url: `/api/inkle/messaging/group/${groupId}/document-request/financial-closings/`,
        params: { channel_id: channelId },
      }),
    }),
    createDocumentRequest: build.mutation<
      DocumentRequest,
      {
        groupId: string;
        payload: DocumentRequestPayload;
      }
    >({
      query: ({ groupId, payload }) => ({
        url: `/api/inkle/messaging/group/${groupId}/document-request/`,
        method: "POST",
        body: payload,
      }),
    }),
    updateDocumentRequest: build.mutation<
      DocumentRequest,
      {
        groupId: string;
        requestId: string;
        payload: Omit<DocumentRequestPayload, "channel_id">;
      }
    >({
      query: ({ groupId, payload, requestId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/document-request/`,
          method: "PUT",
          body: { document_request_id: requestId, ...payload },
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: "DOCUMENT_REQUEST", id: res.uuid }] : [],
    }),
    uploadRequestedDocumentFiles: build.mutation<
      DocumentRequest,
      { requestedDocumentId: string; groupId: string; files: File[] }
    >({
      query: ({ groupId, requestedDocumentId, files }) => {
        const body = new FormData();
        files.forEach((file) => body.append("file", file));

        return {
          url: `/api/inkle/messaging/group/${groupId}/document-request/${requestedDocumentId}/`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: "DOCUMENT_REQUEST", id: res.uuid }] : [],
    }),
    deleteRequestedDocumentFile: build.mutation<
      DocumentRequest,
      { fileUuid: string; groupId: string }
    >({
      query: ({ groupId, fileUuid }) => ({
        url: `/api/inkle/messaging/group/${groupId}/document-request/${fileUuid}/`,
        method: "DELETE",
      }),
      invalidatesTags: (res) =>
        res ? [{ type: "DOCUMENT_REQUEST", id: res.uuid }] : [],
    }),
    getAllDocumentRequests: build.query<
      { pending_requests: DocumentRequest[] },
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/messaging/group/${groupId}/document-request/`,
      providesTags: (res) =>
        res && res.pending_requests.length > 0
          ? res?.pending_requests.map((p) => ({
              type: "DOCUMENT_REQUEST",
              id: p.uuid,
            }))
          : [],
    }),
    getDocumentRequest: build.query<
      DocumentRequest,
      { groupId: string; requestId: string }
    >({
      query: ({ groupId, requestId }) =>
        `/api/inkle/messaging/group/${groupId}/document-request/?document_request_id=${requestId}`,
      providesTags: (res) =>
        res ? [{ type: "DOCUMENT_REQUEST", id: res.uuid }] : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateDocumentRequestMutation,
  useGetAllDocumentRequestsQuery,
  useGetDocumentRequestQuery,
  useGetAllDocumentTypesQuery,
  useUploadRequestedDocumentFilesMutation,
  useDeleteRequestedDocumentFileMutation,
  useUpdateDocumentRequestMutation,
  useGetLinkableFinancialClosingsQuery,
} = extendedApi;
